import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { cn } from '../lib/utils';

const sections = [
  { path: '/', key: 'home', partie: 'I' },
  { path: '/marche', key: 'market', partie: 'I' },
  { path: '/marche-garderies', key: 'marketDaycare', partie: 'I' },
  { path: '/vision', key: 'vision', partie: 'I' },
  { path: '/produit', key: 'product', partie: 'II' },
  { path: '/propriete-intellectuelle', key: 'pi', partie: 'II' },
  { path: '/paiements', key: 'payments', partie: 'II' },
  { path: '/plan-execution', key: 'execution', partie: 'III' },
  { path: '/gtm', key: 'gtm', partie: 'III' },
  { path: '/roadmap', key: 'roadmap', partie: 'III' },
  { path: '/resume-executif', key: 'resume', partie: 'IV' },
  { path: '/modele', key: 'model', partie: 'IV' },
  { path: '/organisation', key: 'org', partie: 'IV' },
  { path: '/valorisation', key: 'valuation', partie: 'IV' },
  { path: '/levee-de-fonds', key: 'fundraising', partie: 'IV' },
  { path: '/synthese-investisseur', key: 'investorSummary', partie: 'IV' },
  { path: '/previsions', key: 'forecast', partie: 'IV' }
];

const sectionNames = {
  fr: {
    home: 'Synthèse Globale',
    market: 'Marché — Écoles',
    marketDaycare: 'Marché — Garderies',
    vision: 'Vision & Objectifs',
    product: 'Produit & Expérience',
    pi: 'Propriété Intellectuelle',
    payments: 'Paiements & Sécurité',
    execution: 'Plan d\'Exécution',
    gtm: 'Go-to-Market',
    roadmap: 'Roadmap & Jalons',
    resume: 'Résumé Exécutif',
    model: 'Modèle d\'Affaires',
    org: 'Organisation & Coûts',
    valuation: 'Valorisation',
    fundraising: 'Levée de Fonds',
    investorSummary: 'Synthèse Investisseur',
    forecast: 'Prévisions Financières'
  },
  en: {
    home: 'Global Overview',
    market: 'Market — Schools',
    marketDaycare: 'Market — Daycares',
    vision: 'Vision & Objectives',
    product: 'Product & Experience',
    pi: 'Intellectual Property',
    payments: 'Payments & Security',
    execution: 'Execution Plan',
    gtm: 'Go-to-Market',
    roadmap: 'Roadmap & Milestones',
    resume: 'Executive Summary',
    model: 'Business Model',
    org: 'Organization & Costs',
    valuation: 'Valuation',
    fundraising: 'Fundraising',
    investorSummary: 'Investor Summary',
    forecast: 'Financial Forecast'
  }
};

export default function NavigationControls() {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = sectionNames[language];

  const currentIndex = sections.findIndex(s => s.path === location.pathname);
  
  // Si on n'est pas sur une page de navigation standard, ne pas afficher les contrôles
  if (currentIndex === -1) {
    return null;
  }
  
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < sections.length - 1;

  const handlePrev = () => {
    if (hasPrev) {
      navigate(sections[currentIndex - 1].path);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
  };

  const handleNext = () => {
    if (hasNext) {
      navigate(sections[currentIndex + 1].path);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' && hasPrev) {
        handlePrev();
      }
      if (e.key === 'ArrowRight' && hasNext) {
        handleNext();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, hasPrev, hasNext]);

  const currentSection = sections[currentIndex];
  const sectionKey = currentSection?.key || 'home';

  return (
    <div className="flex items-center justify-between mt-12 pt-6 border-t border-lime-500/20">
      <Button
        onClick={handlePrev}
        disabled={!hasPrev}
        variant="outline"
        className="border-lime-500/30 text-lime-400 hover:bg-lime-500/10 disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <ChevronLeft className="h-4 w-4 mr-2" />
        {language === 'fr' ? 'Précédent' : 'Previous'}
      </Button>

      <div className="text-center">
        <p className="text-sm text-gray-400">
          {language === 'fr' ? 'Page' : 'Page'} {currentIndex + 1} {language === 'fr' ? 'de' : 'of'} {sections.length}
        </p>
        <p className="text-xs text-lime-500 mt-1">
          {t[sectionKey]}
        </p>
      </div>

      <Button
        onClick={handleNext}
        disabled={!hasNext}
        variant="outline"
        className="border-lime-500/30 text-lime-400 hover:bg-lime-500/10 disabled:opacity-30 disabled:cursor-not-allowed"
      >
        {language === 'fr' ? 'Suivant' : 'Next'}
        <ChevronRight className="h-4 w-4 ml-2" />
      </Button>
    </div>
  );
}
